import React from 'react';
import { Btn, DataTable, Loading, Msg, Section, fmtDate, useAdminGet } from '../../kit';
import { AdminLink } from '../../adminNav';
import { Badge, Drawer } from '../../email/emailKit';
import { Component, LEADS, SIGNAL_LABELS, ScorePill, SegmentPill, ago, fmtSignal } from './shared';

/**
 * One prospect: the score broken into its components, every signal, the
 * suggested nudge and the last 30 dated events across sources
 * (GET leads/prospect_detail).
 */
const ProspectDrawer: React.FC<{
  userId: string; windowDays: string; onClose: () => void;
  onFollowUp: (userId: string, template: string | null) => void;
}> = ({ userId, windowDays, onClose, onFollowUp }) => {
  const q = useAdminGet(LEADS, 'prospect_detail', { user_id: userId, window: windowDays }, [userId, windowDays]);
  const r: any = q.data;
  const parts: Component[] = r?.components || [];
  const signals: Record<string, any> = r?.signals || {};
  return (
    <Drawer title={r ? (r.name || r.email) : 'Prospect'}
      subtitle={r ? `${r.email}${r.organisation ? ` · ${r.organisation}` : ''}` : undefined} onClose={onClose}>
      {q.loading && <Loading />}
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {r && (
        <>
          <div className="flex gap-2 items-center flex-wrap">
            <SegmentPill segment={r.segment} label={r.segment_label} />
            <ScorePill score={r.score} band={r.band} />
            {r.activity_percentile !== null && r.activity_percentile !== undefined && (
              <Badge tone="blue">more active than {r.activity_percentile}% of trial-lapsed</Badge>
            )}
            {!r.signed_in && <Badge tone="grey">never signed in</Badge>}
            {r.is_team && <Badge tone="amber">team</Badge>}
            <AdminLink to={{ group: 'user360', user: r.user_id }}>Open in User 360</AdminLink>
          </div>

          <Section title="Suggested nudge">
            <div className="text-sm text-grayscale-1300">
              <b>{r.suggestion?.label}</b> — {r.suggestion?.why}
              {r.suggestion?.template_key && (
                <span className="text-xs text-grayscale-1100"> (template {r.suggestion.template_key})</span>
              )}
            </div>
            <div>
              <Btn small kind="primary" onClick={() => onFollowUp(r.user_id, r.suggestion?.template_key || null)}
                title="Opens Email Center → Campaigns with this person picked. Nothing is sent from here.">
                Follow up → Campaigns
              </Btn>
            </div>
            {(r.reasons || []).length > 0 && (
              <ul className="list-disc pl-5 text-sm text-grayscale-1200">
                {r.reasons.map((x: string) => <li key={x}>{x}</li>)}
              </ul>
            )}
          </Section>

          <Section title={`Score: ${r.score} of 100`}>
            <div className="flex flex-col gap-1">
              {parts.map(p => (
                <div key={p.key} className="flex items-center gap-2 text-xs">
                  <span className="w-64 truncate" title={p.label}>{p.label}</span>
                  <span className="w-14 text-right text-grayscale-1100">{p.value}</span>
                  <span className="flex-1 h-2 rounded bg-grayscale-200 overflow-hidden">
                    <span className="block h-full bg-primary-500"
                      style={{ width: `${p.weight ? Math.min(100, (100 * p.points) / p.weight) : 0}%` }} />
                  </span>
                  <span className="w-20 text-right"><b>{p.points}</b> / {p.weight}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-grayscale-1100">
              Each line earns up to its weight: weight × log(1 + value) / log(1 + cap) (profile is linear).
              The total is capped at 100.
            </p>
          </Section>

          <Section title="Trial">
            <div className="text-sm text-grayscale-1200 grid grid-cols-2 gap-1">
              <span>Started</span><span>{fmtDate(r.trial_started_at)}</span>
              <span>Ended / lapsed</span><span>{fmtDate(r.trial_ended_at)}</span>
              <span>Origin</span><span>{r.trial_origin || '—'}</span>
              <span>Last active</span><span>{ago(r.last_active_at) || '—'}</span>
              <span>Last checkout opened</span><span>{fmtDate(r.last_checkout_at)}</span>
              {(r.cancel_reasons || []).length > 0 && <>
                <span>Cancel-sheet reasons</span><span>{r.cancel_reasons.join(', ')}{r.cancel_other ? ` — ${r.cancel_other}` : ''}</span>
              </>}
            </div>
          </Section>

          <Section title={`Signals (window: last ${r.window} days)`}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
              {Object.keys(SIGNAL_LABELS).filter(k => k in signals).map(k => (
                <div key={k} className="flex justify-between gap-2 border-b border-grayscale-200 py-0.5">
                  <span className="text-grayscale-1100">{SIGNAL_LABELS[k]}</span><b>{fmtSignal(signals[k])}</b>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Latest events (30, all sources)">
            <DataTable rows={r.events || []} columns={['at', 'kind', 'detail', 'source']} csvName="prospect_events"
              labels={{ at: 'When' }} empty="No events recorded." maxHeight="22rem" />
          </Section>
        </>
      )}
    </Drawer>
  );
};

export default ProspectDrawer;
